import React, { useState, useCallback } from 'react';
import {
  Button,
  Popover,
  Tabs,
  Tab,
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';

export interface DateRangeValue {
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;
}

interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  /** Button variant/size; default outlined, medium */
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

const WEEK_START_DAY = 1; // Monday = 1 in getDay() (Sunday = 0)

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatRangeDisplay(dateFrom: string, dateTo: string): string {
  const from = new Date(dateFrom + 'T12:00:00');
  const to = new Date(dateTo + 'T12:00:00');
  return `${from.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' })} – ${to.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' })}`;
}

/** Monday of the week containing d (week start = Monday). */
function getMondayOfWeek(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : day - WEEK_START_DAY;
  date.setDate(date.getDate() - diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

/** Sunday of the week containing d. */
function getSundayOfWeek(d: Date): Date {
  const monday = getMondayOfWeek(d);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return sunday;
}

function getPresetRanges(): { id: string; label: string; getValue: () => DateRangeValue }[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  return [
    { id: 'today', label: 'Today', getValue: () => ({ dateFrom: toIsoDate(today), dateTo: toIsoDate(today) }) },
    { id: 'yesterday', label: 'Yesterday', getValue: () => ({ dateFrom: toIsoDate(yesterday), dateTo: toIsoDate(yesterday) }) },
    {
      id: 'last7',
      label: 'Last 7 days',
      getValue: () => {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        return { dateFrom: toIsoDate(from), dateTo: toIsoDate(today) };
      },
    },
    {
      id: 'thisWeek',
      label: 'This week',
      getValue: () => {
        const mon = getMondayOfWeek(today);
        const sun = getSundayOfWeek(today);
        return { dateFrom: toIsoDate(mon), dateTo: toIsoDate(sun) };
      },
    },
    {
      id: 'lastWeek',
      label: 'Last week',
      getValue: () => {
        const mon = getMondayOfWeek(today);
        mon.setDate(mon.getDate() - 7);
        const sun = getSundayOfWeek(mon);
        return { dateFrom: toIsoDate(mon), dateTo: toIsoDate(sun) };
      },
    },
    {
      id: 'thisMonth',
      label: 'This month',
      getValue: () => {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { dateFrom: toIsoDate(first), dateTo: toIsoDate(last) };
      },
    },
    {
      id: 'lastMonth',
      label: 'Last month',
      getValue: () => {
        const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const last = new Date(today.getFullYear(), today.getMonth(), 0);
        return { dateFrom: toIsoDate(first), dateTo: toIsoDate(last) };
      },
    },
    {
      id: 'thisQuarter',
      label: 'This quarter',
      getValue: () => {
        const q = Math.floor(today.getMonth() / 3) + 1;
        const first = new Date(today.getFullYear(), (q - 1) * 3, 1);
        const last = new Date(today.getFullYear(), q * 3, 0);
        return { dateFrom: toIsoDate(first), dateTo: toIsoDate(last) };
      },
    },
    {
      id: 'lastQuarter',
      label: 'Last quarter',
      getValue: () => {
        const q = Math.floor(today.getMonth() / 3) + 1;
        const first = new Date(today.getFullYear(), (q - 1) * 3 - 3, 1);
        const last = new Date(today.getFullYear(), (q - 1) * 3, 0);
        return { dateFrom: toIsoDate(first), dateTo: toIsoDate(last) };
      },
    },
    {
      id: 'ytd',
      label: 'Year to date',
      getValue: () => {
        const first = new Date(today.getFullYear(), 0, 1);
        return { dateFrom: toIsoDate(first), dateTo: toIsoDate(today) };
      },
    },
    {
      id: 'last12months',
      label: 'Last 12 months',
      getValue: () => {
        const from = new Date(today);
        from.setFullYear(from.getFullYear() - 1);
        from.setDate(from.getDate() + 1);
        return { dateFrom: toIsoDate(from), dateTo: toIsoDate(today) };
      },
    },
    {
      id: 'last30days',
      label: 'Last 30 days',
      getValue: () => {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        return { dateFrom: toIsoDate(from), dateTo: toIsoDate(today) };
      },
    },
  ];
}

const PRESETS = getPresetRanges();

export default function DateRangeSelector({ value, onChange, size = 'medium', fullWidth }: DateRangeSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tab, setTab] = useState<0 | 1>(0);
  const [customFrom, setCustomFrom] = useState(value.dateFrom);
  const [customTo, setCustomTo] = useState(value.dateTo);

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-selector-popover' : undefined;

  const openPopover = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setCustomFrom(value.dateFrom);
    setCustomTo(value.dateTo);
    setTab(0);
  }, [value.dateFrom, value.dateTo]);

  const closePopover = useCallback(() => setAnchorEl(null), []);

  const applyPreset = useCallback((range: DateRangeValue) => {
    onChange(range);
    closePopover();
  }, [onChange, closePopover]);

  const switchToCustom = useCallback(() => setTab(1), []);

  const applyCustom = useCallback(() => {
    let from = customFrom;
    let to = customTo;
    if (new Date(from) > new Date(to)) {
      [from, to] = [to, from];
    }
    onChange({ dateFrom: from, dateTo: to });
    closePopover();
  }, [customFrom, customTo, onChange, closePopover]);

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        onClick={openPopover}
        startIcon={<CalendarMonth />}
        fullWidth={fullWidth}
        aria-describedby={id}
        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
      >
        {formatRangeDisplay(value.dateFrom, value.dateTo)}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 320, maxHeight: 420 } }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v as 0 | 1)} variant="fullWidth">
          <Tab label="Presets" />
          <Tab label="Custom" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 && (
            <List dense disablePadding>
              {PRESETS.map((preset) => (
                <ListItemButton key={preset.id} onClick={() => applyPreset(preset.getValue())}>
                  <ListItemText primary={preset.label} />
                </ListItemButton>
              ))}
              <ListItemButton onClick={switchToCustom}>
                <ListItemText primary="Custom…" />
              </ListItemButton>
            </List>
          )}
          {tab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                type="date"
                size="small"
                label="From"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                <Button size="small" onClick={closePopover}>
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={applyCustom}>
                  Apply
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
}
